import { AfterViewInit, Component, DestroyRef, inject, Injector, Input, OnInit, Type, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Entity } from '@app/core/model/entities/entity';
import {
  FIELD_ENTITY_INJECTION,
  FIELD_EVENTS_ORIGIN,
  FIELD_EXTRA_DATA,
  FIELD_GROUP_CONFIG_INJECTION,
  FIELD_PERMISSIONS_INJECTION,
  FIELD_PRECONDITIONS_INJECTION,
  Section,
  SECTION_CREATES_ENTITY_INJECTION,
  SECTION_ENTITY_TYPE_INJECTION
} from '@app/core/model/other/field-config';
import { AbstractFieldGroupBuilder } from '@app/shared/components/fields/abstract.field';
import { getFieldGroupBuilder } from '@app/shared/components/fields/field-builder-selector';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { SectionAnchorDirective } from '@app/shared/components/form-builder/sections/section-anchor.directive';
import { SectionBuilderComponent } from '@app/shared/components/form-builder/sections/section-builder.component';
import { ValidationService } from '@app/shared/services/validation.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SectionService } from '@services/section.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EMPTY, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  standalone: false
})
export class FormBuilderComponent implements OnInit, AfterViewInit {
  private _form: Observable<Section[]>;
  protected appManager = inject(AppManager);
  protected accessManager = inject(AccessManager);
  private validationService = inject(ValidationService);
  private formStateService = inject(FormStateService);
  protected deviceDetectorService = inject(DeviceDetectorService);
  private sectionService = inject(SectionService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  @Input() public formId: string;
  @Input() public form: Section[] = null;
  @Input() public displayHeader: boolean = true;
  @Input() public permissionsForEdition: string[];
  @Input() public readOnly: Subject<boolean>;
  @Input() public eventsOrigin: EventOriginEnum = EventOriginEnum.SIDEPANEL;
  /**
   * @deprecated Use another form template instead. TTT-3204
   */
  @Input() public fieldsToHide: string[] = [];

  @ViewChild(SectionAnchorDirective, {static: true}) public sectionHost: SectionAnchorDirective;

  private sections: Section[] = [];

  public ngOnInit(): void {
    this._form = this.form ? of(this.form) : this.sectionService.getSectionsByFormCode(this.formId);
    this.sectionHost.viewContainerRef.clear();
  }

  public ngOnDestroy(): void {
    this.formStateService.cleanUp();
  }

  public ngAfterViewInit(): void {
    this.loadSections();
  }

  private loadSections(): void {
    this._form.pipe(
      switchMap(sections => {
        this.sections = sections;
        return merge(
          this.appManager.entityObservable$,
          this.readOnly ? this.readOnly.asObservable().pipe(map((bool) => !bool)) : EMPTY
        );
      }),
      filter(() => !!this.appManager.currentEntity),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(preconditionsForEdition => {
      // Initialize the hook objects
      ['GTE_OTHER_FIELD_VALUE', 'LTE_OTHER_FIELD_VALUE', 'AFTER_OTHER_DATE', 'BEFORE_OTHER_DATE', 'changes']
        .forEach(hook => {
          if (!this.formStateService.getPath([hook])) {
            this.formStateService.setPath([hook], {});
          }
        });
      if (!this.formStateService.getPath(['computed'])) {
        this.formStateService.setPath(['computed'], new ReplaySubject<void>());
      }

      // For each section, create a section component and inject
      this.sectionHost.viewContainerRef.clear();
      const sectionEntity: Entity = this.appManager.currentEntity;
      this.sections.filter(section => {
        const validConditions = this.validationService.validateConditions(sectionEntity, section.conditionsToView);
        const validAssetTypes = section.allAssetTypes || (
          isAssetRelatedEntity(sectionEntity)
          && sectionEntity.relatedAssets.some(({typeId}) => {
            return section.assetTypes.map(assetType => assetType.id).includes(typeId);
          })
        );
        return validConditions && validAssetTypes;
      })
        .forEach((section) => {

          const filteredFieldGroups = section.children
            .filter(fieldGroup => {
              // Filter fields that have been passed in fieldsToHide
              return !this.fieldsToHide.includes(fieldGroup.code) // FIXME Replace list of fields to hide with a list of conditions TTT-3204
                // Filter fields that don't fulfill the conditions to view
                && this.validationService.validateConditions(sectionEntity, fieldGroup.conditionsToView)
                // Filter fields that the current user has no read access to.
                && fieldGroup.children.some(fc => this.accessManager.hasFieldReadAccess(fc.code, fc.fieldEntityType));
            });
          // If there are no fields to show, return
          if (filteredFieldGroups.length === 0) {
            return;
          }

          const sectionRef = this.sectionHost.viewContainerRef.createComponent(SectionBuilderComponent, {
            injector: Injector.create({
              parent: this.injector,
              providers: [{
                provide: FIELD_EXTRA_DATA,
                useValue: {
                  sectionLabel: section.label,
                  displayHeader: this.displayHeader,
                  sectionEmptyLabel: section.emptyLabel
                }
              }]
            })
          });

          const fieldGroupContainerRef = sectionRef.instance.fieldGroupHost.viewContainerRef;
          fieldGroupContainerRef.clear();

          // Calculate section's conditions to edit
          filteredFieldGroups
            .forEach(fieldGroup => {
              // Make sure there are actual fields in the group
              if (fieldGroup.children.length > 0) {
                // Get the field type to create
                const fieldGroupTypeComponent: Type<AbstractFieldGroupBuilder> = getFieldGroupBuilder(fieldGroup);

                if (fieldGroupTypeComponent) {
                  // Init path for sub-entity
                  if (fieldGroup.propertiesPath && !this.formStateService.getPath([fieldGroup.propertiesPath])) {
                    this.formStateService.setPath([fieldGroup.propertiesPath], {});
                  }

                  // FIXME: Get propertiesPath from section at line 93 TTT-3666
                  let fieldGroupEntity = void 0;
                  if (!section.customOptions.isCreatingEntity) {
                    fieldGroupEntity = this.appManager.currentEntity[fieldGroup.propertiesPath] ?? this.appManager.currentEntity;
                  }

                  const customEditPermissions = this.accessManager.getCustomEditPermissionByEntityType(fieldGroupEntity);
                  // Create the fieldgroup component and inject the entity and fieldgroup configuration
                  const fieldGroupRef = fieldGroupContainerRef.createComponent(fieldGroupTypeComponent, {
                    injector: Injector.create({
                      parent: this.injector, providers: [
                        {provide: FIELD_GROUP_CONFIG_INJECTION, useValue: fieldGroup},
                        {provide: FIELD_ENTITY_INJECTION, useValue: fieldGroupEntity},
                        {provide: SECTION_ENTITY_TYPE_INJECTION, useValue: section.entityType},
                        {provide: SECTION_CREATES_ENTITY_INJECTION, useValue: section.customOptions.isCreatingEntity},
                        {
                          provide: FIELD_PRECONDITIONS_INJECTION,
                          useValue: preconditionsForEdition &&
                            this.validationService.validateConditions(sectionEntity, section.conditionsToEdit) &&
                            this.validationService.validateConditions(fieldGroupEntity, fieldGroup.conditionsToEdit)
                        },
                        {
                          provide: FIELD_PERMISSIONS_INJECTION,
                          useValue: customEditPermissions || this.permissionsForEdition
                        },
                        {
                          provide: FIELD_EXTRA_DATA,
                          useValue: {
                            propertiesPath: fieldGroup.propertiesPath,
                            fieldGroupCode: fieldGroup.code,
                            formId: this.formId
                          }
                        },
                        {provide: FIELD_EVENTS_ORIGIN, useValue: this.eventsOrigin}
                      ]
                    })
                  });
                  fieldGroupRef.changeDetectorRef.detectChanges();
                  sectionRef.instance.fieldGroupRefs.push(fieldGroupRef);
                }
              }

            });
        });
      this.formStateService.endLoad();
    });
  }
}
